/* ==================================================
   PEGUU — MODAL DE FACTURACIÓN
================================================== */

(() => {
  "use strict";


  /* ==================================================
     CONFIGURACIÓN
  ================================================== */

  const BILLING_STORAGE_KEY =
    "peguu:billing-data";

  const BILLING_PROFILE_API_URL =
    window.PeguuApi.endpoint("billingProfile");

  const AUTHENTICATED_CFDI_USES =
    new Set([
      "G01",
      "G03",
      "S01"
    ]);

  const RFC_PATTERN =
    /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/;

  const POSTAL_CODE_PATTERN =
    /^[0-9]{5}$/;


  /* ==================================================
     UTILIDADES
  ================================================== */

  function normalizeValue(value) {
    return String(value ?? "")
      .trim();
  }


  function readBillingData() {

    try {

      const stored =
        sessionStorage.getItem(
          BILLING_STORAGE_KEY
        );


      return stored
        ? JSON.parse(stored)
        : null;

    } catch (error) {

      return null;

    }

  }


  function saveBillingData(data) {

    try {

      sessionStorage.setItem(
        BILLING_STORAGE_KEY,
        JSON.stringify(data)
      );


      return true;

    } catch (error) {

      console.warn(
        "PEGUU: no fue posible guardar los datos de facturación.",
        error
      );


      return false;

    }

  }


  /* ==================================================
     INICIALIZAR
  ================================================== */

  function initializeBillingModal(root) {

    if (
      !root ||
      root.dataset.billingInitialized ===
        "true"
    ) {

      return;

    }


    root.dataset.billingInitialized =
      "true";


    const dialog =
      root.querySelector(
        ".peguu-billing-modal__dialog"
      );


    const form =
      root.querySelector(
        "[data-peguu-billing-form]"
      );


    const closeButtons =
      root.querySelectorAll(
        "[data-peguu-billing-close]"
      );


    const cancelButton =
      root.querySelector(
        "[data-peguu-billing-cancel]"
      );


    const message =
      root.querySelector(
        "[data-peguu-billing-message]"
      );


    const businessNameInput =
      root.querySelector(
        "[data-peguu-billing-business-name]"
      );


    const rfcInput =
      root.querySelector(
        "[data-peguu-billing-rfc]"
      );


    const postalCodeInput =
      root.querySelector(
        "[data-peguu-billing-postal-code]"
      );


    const addressInput =
      root.querySelector(
        "[data-peguu-billing-address]"
      );


    const taxRegimeInput =
      root.querySelector(
        "[data-peguu-billing-tax-regime]"
      );


    const cfdiUseInput =
      root.querySelector(
        "[data-peguu-billing-cfdi-use]"
      );


    const acceptButton =
      root.querySelector(
        "[data-peguu-billing-accept]"
      );


    let activeCheckbox =
      null;


    let lastFocusedElement =
      null;


    let billingProfileId =
      "";


    let billingProfileData =
      null;


    let profileLoadPromise =
      null;


    let isSavingProfile =
      false;


    /* ==================================================
       CAMPOS
    ================================================== */

    function getFields() {

      return Array.from(
        root.querySelectorAll(
          "[data-peguu-billing-field]"
        )
      );

    }


    function getFieldInput(field) {

      return field?.querySelector(
        "input, select"
      ) || null;

    }


    function getFieldError(field) {

      return field?.querySelector(
        "[data-peguu-billing-error]"
      ) || null;

    }


    /* ==================================================
       MENSAJE
    ================================================== */

    function hideMessage() {

      if (!message) {
        return;
      }


      message.hidden =
        true;


      message.textContent =
        "";


      message.classList.remove(
        "is-error",
        "is-success"
      );

    }


    function showMessage(
      text,
      type = "error"
    ) {

      if (!message) {
        return;
      }


      message.hidden =
        false;


      message.textContent =
        text;


      message.classList.remove(
        "is-error",
        "is-success"
      );


      message.classList.add(
        `is-${type}`
      );

    }


    /* ==================================================
       ERRORES
    ================================================== */

    function clearFieldError(field) {

      if (!field) {
        return;
      }


      const input =
        getFieldInput(field);


      const error =
        getFieldError(field);


      field.classList.remove(
        "is-error"
      );


      input?.removeAttribute(
        "aria-invalid"
      );


      if (error) {
        error.hidden = true;
      }

    }


    function markFieldError(field) {

      if (!field) {
        return;
      }


      const input =
        getFieldInput(field);


      const error =
        getFieldError(field);


      field.classList.add(
        "is-error"
      );


      input?.setAttribute(
        "aria-invalid",
        "true"
      );


      if (error) {
        error.hidden = false;
      }

    }


    function clearAllErrors() {

      getFields().forEach(
        clearFieldError
      );


      hideMessage();

    }


    /* ==================================================
       VALIDACIÓN
    ================================================== */

    function isInputValid(input) {

      if (!input) {
        return false;
      }


      const value =
        normalizeValue(
          input.value
        );


      if (
        input.required &&
        !value
      ) {

        return false;

      }


      if (
        input === rfcInput
      ) {

        return RFC_PATTERN.test(
          value.toUpperCase()
        );

      }


      if (
        input === postalCodeInput
      ) {

        return POSTAL_CODE_PATTERN.test(
          value
        );

      }


      return true;

    }


    function validateForm() {

      let firstInvalidInput =
        null;


      let isValid =
        true;


      getFields().forEach(
        (
          field
        ) => {

          const input =
            getFieldInput(field);


          clearFieldError(field);


          if (
            !isInputValid(input)
          ) {

            markFieldError(field);


            isValid =
              false;


            if (
              !firstInvalidInput
            ) {

              firstInvalidInput =
                input;

            }

          }

        }
      );


      if (
        firstInvalidInput
      ) {

        firstInvalidInput.focus();

      }


      return isValid;

    }


    /* ==================================================
       OBTENER DATOS
    ================================================== */

    function getFormData() {

      return {

        businessName:
          normalizeValue(
            businessNameInput?.value
          ),

        rfc:
          normalizeValue(
            rfcInput?.value
          )
            .toUpperCase(),

        postalCode:
          normalizeValue(
            postalCodeInput?.value
          ),

        address:
          normalizeValue(
            addressInput?.value
          ),

        taxRegime:
          normalizeValue(
            taxRegimeInput?.value
          ),

        taxRegimeLabel:
          normalizeValue(
            taxRegimeInput
              ?.selectedOptions?.[0]
              ?.textContent
          ),

        cfdiUse:
          normalizeValue(
            cfdiUseInput?.value
          ),

        cfdiUseLabel:
          normalizeValue(
            cfdiUseInput
              ?.selectedOptions?.[0]
              ?.textContent
          ),

        updatedAt:
          new Date()
            .toISOString()

      };

    }


    function getCustomerSession() {

      return window.PeguuAuth
        ?.getSession?.() ||
        null;

    }


    function syncCfdiUseOptions(
      session = getCustomerSession()
    ) {

      if (!cfdiUseInput) {
        return;
      }


      const restrictOptions =
        Boolean(session?.token);


      Array.from(
        cfdiUseInput.options
      ).forEach((option) => {

        const isAllowed =
          !option.value ||
          AUTHENTICATED_CFDI_USES
            .has(option.value);

        const isRestricted =
          restrictOptions &&
          !isAllowed;


        option.disabled =
          isRestricted;

        option.hidden =
          isRestricted;

      });


      if (
        restrictOptions &&
        cfdiUseInput.value &&
        !AUTHENTICATED_CFDI_USES
          .has(cfdiUseInput.value)
      ) {
        cfdiUseInput.value = "";
      }

    }


    function getProfileCandidate(
      responseData
    ) {

      const candidates = [
        responseData?.data
          ?.billingProfile,
        responseData?.data
          ?.profile,
        responseData?.data,
        responseData?.billingProfile,
        responseData?.profile,
        responseData
      ];


      return candidates.find(
        (candidate) =>
          candidate &&
          typeof candidate ===
            "object" &&
          (
            candidate.id ||
            candidate.billingProfileId ||
            candidate.legalName ||
            candidate.rfc ||
            candidate.postalCode ||
            candidate.cfdiUse ||
            candidate.taxRegime
          )
      ) || null;

    }


    function getProfileId(
      responseData,
      profile
    ) {

      return normalizeValue(
        profile?.id ||
        profile?.billingProfileId ||
        responseData?.data?.id ||
        responseData?.data
          ?.billingProfileId ||
        responseData
          ?.billingProfileId ||
        responseData?.id
      );

    }


    function mapProfileToLocalData(
      profile
    ) {

      return {
        businessName:
          normalizeValue(
            profile?.legalName ||
            profile?.businessName
          ),
        rfc:
          normalizeValue(
            profile?.rfc
          ).toUpperCase(),
        postalCode:
          normalizeValue(
            profile?.postalCode
          ),
        address:
          normalizeValue(
            profile?.address
          ),
        taxRegime:
          normalizeValue(
            profile?.taxRegime
          ),
        cfdiUse:
          normalizeValue(
            profile?.cfdiUse
          )
      };

    }


    function buildBillingProfilePayload(
      billingData
    ) {

      return {
        legalName:
          normalizeValue(
            billingData?.businessName
          ),
        rfc:
          normalizeValue(
            billingData?.rfc
          ).toUpperCase(),
        postalCode:
          normalizeValue(
            billingData?.postalCode
          ),
        address:
          normalizeValue(
            billingData?.address
          ),
        cfdiUse:
          normalizeValue(
            billingData?.cfdiUse
          ),
        taxRegime:
          normalizeValue(
            billingData?.taxRegime
          )
      };

    }


    function applyBillingProfile(
      responseData,
      fallbackProfile = null
    ) {

      const responseProfile =
        getProfileCandidate(
          responseData
        );

      const profile =
        (
          responseProfile ||
          fallbackProfile
        )
          ? {
              ...(
                fallbackProfile ||
                {}
              ),
              ...(
                responseProfile ||
                {}
              )
            }
          : null;

      const profileId =
        getProfileId(
          responseData,
          responseProfile
        ) ||
        billingProfileId;


      if (
        !profile ||
        !profileId
      ) {
        return null;
      }


      billingProfileId =
        profileId;

      billingProfileData =
        mapProfileToLocalData(
          profile
        );


      fillForm(
        billingProfileData
      );


      syncCfdiUseOptions();


      const localData =
        getFormData();


      billingProfileData =
        localData;


      saveBillingData(
        localData
      );


      return {
        id:
          billingProfileId,
        data:
          billingProfileData
      };

    }


    async function loadBillingProfile(
      session = getCustomerSession()
    ) {

      if (!session?.token) {
        return null;
      }


      if (profileLoadPromise) {
        return profileLoadPromise;
      }


      profileLoadPromise =
        (async () => {

          const response =
            await window.PeguuAuthClient.authFetch(
              BILLING_PROFILE_API_URL,
              {
                method: "GET",
                headers: {
                  Accept:
                    "application/json"
                }
              }
            );


          let responseData = null;


          try {
            responseData =
              await response.json();
          } catch (error) {
            responseData = null;
          }


          if (response.status === 404) {
            billingProfileId = "";
            billingProfileData = null;
            sessionStorage.removeItem(
              BILLING_STORAGE_KEY
            );
            return null;
          }


          if (
            !response.ok ||
            responseData?.success ===
              false
          ) {
            const apiError =
              new Error(
                responseData?.message ||
                "No fue posible consultar tus datos de facturación."
              );

            apiError.status =
              response.status;

            throw apiError;
          }


          const appliedProfile =
            applyBillingProfile(
              responseData
            );


          if (!appliedProfile) {
            billingProfileId = "";
            billingProfileData = null;
            sessionStorage.removeItem(
              BILLING_STORAGE_KEY
            );
            return null;
          }


          window.dispatchEvent(
            new CustomEvent(
              "peguu:billing-profile-loaded",
              {
                detail:
                  appliedProfile
              }
            )
          );


          return appliedProfile;

        })();


      try {
        return await profileLoadPromise;
      } finally {
        profileLoadPromise = null;
      }

    }


    async function saveBillingProfile(
      billingData,
      session
    ) {

      const requestData =
        buildBillingProfilePayload(
          billingData
        );


      const response =
        await window.PeguuAuthClient.authFetch(
          BILLING_PROFILE_API_URL,
          {
            method: "PUT",
            headers: {
              "Content-Type":
                "application/json",
              Accept:
                "application/json"
            },
            body:
              JSON.stringify(
                requestData
              )
          }
        );


      let responseData = null;


      try {
        responseData =
          await response.json();
      } catch (error) {
        responseData = null;
      }


      if (
        !response.ok ||
        responseData?.success ===
          false
      ) {
        const apiError =
          new Error(
            responseData?.message ||
            "No fue posible guardar tus datos de facturación."
          );

        apiError.status =
          response.status;

        throw apiError;
      }


      const appliedProfile =
        applyBillingProfile(
          responseData,
          requestData
        );


      if (!appliedProfile?.id) {
        throw new Error(
          "El perfil fiscal se guardó, pero la respuesta no incluyó su identificador."
        );
      }


      return appliedProfile;

    }


    /* ==================================================
       LLENAR FORMULARIO
    ================================================== */

    function fillForm(data) {

      if (!data) {
        return;
      }


      if (businessNameInput) {
        businessNameInput.value =
          data.businessName || "";
      }


      if (rfcInput) {
        rfcInput.value =
          data.rfc || "";
      }


      if (postalCodeInput) {
        postalCodeInput.value =
          data.postalCode || "";
      }


      if (addressInput) {
        addressInput.value =
          data.address || "";
      }


      if (taxRegimeInput) {
        taxRegimeInput.value =
          data.taxRegime || "";
      }


      if (cfdiUseInput) {
        cfdiUseInput.value =
          data.cfdiUse || "";
      }

    }


    /* ==================================================
       ABRIR
    ================================================== */

    function openModal(checkbox = null) {

      activeCheckbox =
        checkbox;


      lastFocusedElement =
        document.activeElement
          instanceof HTMLElement
          ? document.activeElement
          : null;


      fillForm(
        billingProfileData ||
        readBillingData()
      );


      syncCfdiUseOptions();


      clearAllErrors();


      root.classList.add(
        "is-open"
      );


      root.setAttribute(
        "aria-hidden",
        "false"
      );


      document.body.classList.add(
        "peguu-billing-modal-open"
      );


      window.setTimeout(
        () => {

          businessNameInput?.focus();

        },
        70
      );

    }


    /* ==================================================
       CERRAR
    ================================================== */

    function closeModal() {

      root.classList.remove(
        "is-open"
      );


      root.setAttribute(
        "aria-hidden",
        "true"
      );


      document.body.classList.remove(
        "peguu-billing-modal-open"
      );


      clearAllErrors();


      if (
        lastFocusedElement &&
        typeof lastFocusedElement.focus ===
          "function"
      ) {

        lastFocusedElement.focus();

      }

    }


    /* ==================================================
       CANCELAR
    ================================================== */

    function cancelModal() {

      const savedData =
        readBillingData();


      /*
       * Si todavía no había datos
       * aceptados, desmarcar factura.
       */

      if (
        activeCheckbox &&
        !savedData
      ) {

        activeCheckbox.checked =
          false;


        activeCheckbox.dispatchEvent(
          new Event(
            "change",
            {
              bubbles: true
            }
          )
        );

      }


      closeModal();

    }


    /* ==================================================
       ACEPTAR
    ================================================== */

    async function handleSubmit(event) {

      event.preventDefault();


      clearAllErrors();


      if (
        !validateForm()
      ) {

        showMessage(
          "Revisa los datos fiscales marcados."
        );


        return;

      }


      const billingData =
        getFormData();


      const session =
        getCustomerSession();


      if (session?.token) {

        if (isSavingProfile) {
          return;
        }


        isSavingProfile = true;


        if (acceptButton) {
          acceptButton.disabled =
            true;
        }


        showMessage(
          "Guardando tus datos fiscales…",
          "success"
        );


        try {
          await saveBillingProfile(
            billingData,
            session
          );
        } catch (error) {
          const errorMessage =
            error?.status === 401
              ? "Tu sesión terminó. Inicia sesión nuevamente para guardar tus datos fiscales."
              : error?.message ||
                "No fue posible guardar tus datos de facturación.";


          showMessage(
            errorMessage
          );


          return;
        } finally {
          isSavingProfile = false;


          if (acceptButton) {
            acceptButton.disabled =
              false;
          }
        }

      }


      if (
        !saveBillingData(
          billingData
        )
      ) {

        showMessage(
          "No fue posible guardar los datos de facturación."
        );


        return;

      }


      if (
        activeCheckbox
      ) {

        activeCheckbox.checked =
          true;

      }


      window.dispatchEvent(
        new CustomEvent(
          "peguu:billing-saved",
          {
            detail:
              billingData
          }
        )
      );


      closeModal();

    }


    /* ==================================================
       SANITIZAR RFC
    ================================================== */

    function sanitizeRfc() {

      if (!rfcInput) {
        return;
      }


      rfcInput.value =
        rfcInput.value
          .toUpperCase()
          .replace(
            /[^A-ZÑ&0-9]/g,
            ""
          )
          .slice(
            0,
            13
          );

    }


    /* ==================================================
       SANITIZAR CP
    ================================================== */

    function sanitizePostalCode() {

      if (!postalCodeInput) {
        return;
      }


      postalCodeInput.value =
        postalCodeInput.value
          .replace(
            /\D/g,
            ""
          )
          .slice(
            0,
            5
          );

    }


    /* ==================================================
       CHECKBOX DE FACTURA
    ================================================== */

    document.addEventListener(
      "change",
      (
        event
      ) => {

        const checkbox =
          event.target.closest(
            "[data-needs-invoice]"
          );


        if (!checkbox) {
          return;
        }


        if (
          checkbox.checked
        ) {

          openModal(
            checkbox
          );

        } else {

          sessionStorage.removeItem(
            BILLING_STORAGE_KEY
          );

        }

      }
    );


    /* ==================================================
       EVENTOS
    ================================================== */

    closeButtons.forEach(
      (
        button
      ) => {

        button.addEventListener(
          "click",
          cancelModal
        );

      }
    );


    cancelButton
      ?.addEventListener(
        "click",
        cancelModal
      );


    form
      ?.addEventListener(
        "submit",
        handleSubmit
      );


    rfcInput
      ?.addEventListener(
        "input",
        sanitizeRfc
      );


    postalCodeInput
      ?.addEventListener(
        "input",
        sanitizePostalCode
      );


    getFields().forEach(
      (
        field
      ) => {

        const input =
          getFieldInput(field);


        input?.addEventListener(
          "input",
          () => {

            clearFieldError(
              field
            );


            hideMessage();

          }
        );


        input?.addEventListener(
          "change",
          () => {

            clearFieldError(
              field
            );


            hideMessage();

          }
        );

      }
    );


    document.addEventListener(
      "keydown",
      (
        event
      ) => {

        if (
          event.key === "Escape" &&
          root.classList.contains(
            "is-open"
          )
        ) {

          cancelModal();

        }

      }
    );


    /* ==================================================
       API PÚBLICA
    ================================================== */

    window.PeguuBilling = {

      open:
        openModal,

      close:
        closeModal,

      getData:
        readBillingData,

      clear() {

        sessionStorage.removeItem(
          BILLING_STORAGE_KEY
        );

      },

      loadProfile:
        loadBillingProfile,

      getProfileId() {

        return billingProfileId;

      },

      hasPersistedProfile() {

        return Boolean(
          billingProfileId &&
          billingProfileData
            ?.businessName &&
          billingProfileData?.rfc &&
          billingProfileData
            ?.postalCode &&
          billingProfileData
            ?.cfdiUse &&
          billingProfileData
            ?.taxRegime
        );

      },

      getProfileData() {

        return billingProfileData;

      },

      showProfileError(
        checkbox = null
      ) {

        openModal(
          checkbox
        );


        showMessage(
          "Guarda correctamente tus datos fiscales antes de continuar."
        );

      },

      resetProfileState() {

        billingProfileId = "";
        billingProfileData = null;

      }

    };

  }


  /* ==================================================
     INICIALIZAR TODOS
  ================================================== */

  function initializeAllBillingModals(
    scope = document
  ) {

    const roots =
      scope.matches?.(
        "[data-peguu-billing-modal]"
      )
        ? [scope]
        : Array.from(
            scope.querySelectorAll(
              "[data-peguu-billing-modal]"
            )
          );


    roots.forEach(
      initializeBillingModal
    );

  }


  /* ==================================================
     DOM READY
  ================================================== */

  if (
    document.readyState ===
    "loading"
  ) {

    document.addEventListener(
      "DOMContentLoaded",
      () => {

        initializeAllBillingModals();

      }
    );

  } else {

    initializeAllBillingModals();

  }


  /* ==================================================
     SHOPIFY THEME EDITOR
  ================================================== */

  document.addEventListener(
    "shopify:section:load",
    (
      event
    ) => {

      initializeAllBillingModals(
        event.target
      );

    }
  );

})();
